/**
 * Customer Pricelists saved-search persistence. Same shape/behavior as
 * `open-orders`/`cofa-documents`'s `utils/saved-searches.ts` — this module's
 * only filter is the list search string, so the saved "filter" is just
 * that string rather than a full filter object.
 *
 * localStorage-backed, capped at 20 entries.
 */

const STORAGE_KEY = 'spi-ops:customer-pricelists:saved-searches'

export interface SavedSearch {
  id: string
  label: string
  query: string
  createdAt: string
}

export function loadSaved(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed as SavedSearch[]
  } catch {
    return []
  }
}

function persistSaved(list: SavedSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    // quota / private mode — non-fatal
  }
}

export function addSaved(label: string, query: string): SavedSearch[] {
  const list = loadSaved()
  const item: SavedSearch = {
    id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label,
    query,
    createdAt: new Date().toISOString(),
  }
  const next = [item, ...list].slice(0, 20)
  persistSaved(next)
  return next
}

export function removeSaved(id: string): SavedSearch[] {
  const next = loadSaved().filter((s) => s.id !== id)
  persistSaved(next)
  return next
}

export function defaultLabelFor(query: string): string {
  return query.trim() ? `"${query.trim()}"` : 'All customers'
}
